import React, { useState, useEffect } from 'react';
import { FaLayerGroup, FaEdit, FaTrash, FaEye, FaPlus, FaSearch, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from '../api/axios';

const StatCard = ({ title, value, icon, color }) => (
  <div className={`bg-gradient-to-r ${color} text-white p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105`}>
    <div className="flex justify-between items-center">
      <div>
        <p className="text-sm">{title}</p>
        <h3 className="text-3xl font-bold">{value}</h3>
      </div>
      <div className="text-4xl opacity-80">{icon}</div>
    </div>
  </div>
);

export default function DivisionesTable() {
  const [divisiones, setDivisiones] = useState([]);
  const [filteredDivisiones, setFilteredDivisiones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchDivisiones();
  }, []);

  useEffect(() => {
    filterAndSortDivisiones();
  }, [searchTerm, divisiones, sortColumn, sortDirection]);

  const fetchDivisiones = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/divisiones');
      setDivisiones(response.data);
      setFilteredDivisiones(response.data);
    } catch (err) {
      setError('Error al cargar las areas. Por favor, intente de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortDivisiones = () => {
    let filtered = divisiones.filter(division =>
      division.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = filtered.sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredDivisiones(sorted);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const handleDeleteDivision = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este area?')) {
      setIsLoading(true);
      setError(null);
      try {
        await axios.delete(`/divisiones/${id}`);
        fetchDivisiones();
      } catch (err) {
        setError('Error al eliminar el area. Por favor, intente de nuevo.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredDivisiones.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredDivisiones.length / itemsPerPage);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center text-gray-800 font-serif">Gestión de Areas</h1>
      <div className="flex justify-center mb-8">
        <StatCard title="Total areas" value={divisiones.length} icon={<FaLayerGroup />} color="from-blue-500 to-blue-700" />
      </div>
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Areas</h2>
          <Link to="/home/divisiones/add" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">
            <FaPlus className="mr-2" /> Añadir Area
          </Link>
        </div>
        <div className="p-4 flex items-center bg-gray-100">
          <FaSearch className="mr-2" />
          <input
            type="text"
            placeholder="Buscar por descripción"
            className="border p-2 rounded w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blue-500 text-white">
              <tr>
                {['id', 'descripcion', ''].map((col) => (
                  <th
                    key={col}
                    className="px-6 py-3 cursor-pointer"
                    onClick={() => handleSort(col)}
                  >
                    {col.charAt(0).toUpperCase() + col.slice(1)}{' '}
                    {sortColumn === col &&
                      (sortDirection === 'asc' ? (
                        <FaSortAmountUp />
                      ) : (
                        <FaSortAmountDown />
                      ))}
                  </th>
                ))}
                <th className="px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((division) => (
                <tr key={division.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">{division.id}</td>
                  <td className="px-6 py-4">{division.descripcion}</td>
                  <td className="px-6 py-4">{division.lugareId}</td>
                  <td className="px-6 py-4 flex space-x-2">
                    <Link to={`/home/divisiones/${division.id}`} className="text-blue-600 hover:text-blue-900"><FaEye /></Link>
                    <Link to={`/home/divisiones/edit/${division.id}`} className="text-green-600 hover:text-green-900"><FaEdit /></Link>
                    <button onClick={() => handleDeleteDivision(division.id)} className="text-red-600 hover:text-red-900"><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-center space-x-2 p-4">
          {[...Array(totalPages).keys()].map((num) => (
            <button
              key={num + 1}
              onClick={() => setCurrentPage(num + 1)}
              className={`px-3 py-1 rounded ${currentPage === num + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
            >
              {num + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
